import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Users } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { supabase } from '@/integrations/supabase/client';

interface Whisper {
  id: string;
  text: string;
  x: number;
  y: number;
  opacity: number;
  size: number;
  created_at: string;
}

interface WhisperWallProps {
  className?: string;
}

const WhisperWall = ({ className = '' }: WhisperWallProps) => {
  const { t } = useLanguage();
  const [whispers, setWhispers] = useState<Whisper[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load whispers from Supabase
  useEffect(() => {
    const fetchWhispers = async () => {
      const { data, error } = await supabase
        .from('whispers')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (!error && data) {
        setWhispers(data);
      }
      setLoading(false);
    };

    fetchWhispers();

    // Subscribe to real-time whispers
    const channel = supabase
      .channel('whispers-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'whispers' },
        (payload) => {
          setWhispers(prev => [payload.new as Whisper, ...prev].slice(0, 20));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const handleSubmit = async () => {
    const text = inputValue.trim();
    if (!text || text.length > 100) return;

    const { error } = await supabase.from('whispers').insert({
      text,
      x: Math.random() * 80 + 10,
      y: Math.random() * 60 + 20,
      opacity: 0.4 + Math.random() * 0.4,
      size: 12 + Math.floor(Math.random() * 4),
    });

    if (!error) {
      setInputValue('');
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
    }
  };

  return (
    <div className={`border border-border/10 rounded-xl overflow-hidden ${className}`}>
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-3.5 h-3.5 text-muted-foreground/25" />
          <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground/30">
            {t('whisper_wall') || 'Whisper Wall'}
          </span>
        </div>
        <span className="text-[9px] text-muted-foreground/15">
          {whispers.length} {t('whispers_count') || 'whispers'}
        </span>
      </div>

      {/* Whisper field */}
      <div className="relative h-48 bg-card/20 overflow-hidden">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[10px] text-muted-foreground/15 tracking-widest animate-pulse">...</span>
          </div>
        ) : (
          <AnimatePresence>
            {whispers.slice(0, 12).map((whisper, i) => (
              <motion.p
                key={whisper.id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: whisper.opacity * 0.6, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="absolute text-muted-foreground italic pointer-events-none select-none"
                style={{
                  left: `${whisper.x}%`,
                  top: `${whisper.y}%`,
                  fontSize: `${whisper.size}px`,
                  transform: 'translate(-50%, -50%)',
                  maxWidth: '45%',
                }}
              >
                "{whisper.text}"
              </motion.p>
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Input */}
      <div className="p-3 border-t border-border/8">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value.slice(0, 100))}
            placeholder={t('leave_whisper') || 'Leave a whisper...'}
            className="flex-1 bg-transparent border border-border/10 rounded-lg px-3 py-2 text-xs placeholder:text-muted-foreground/15 focus:outline-none focus:border-border/25 transition-colors"
            maxLength={100}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          />
          <button
            onClick={handleSubmit}
            disabled={!inputValue.trim()}
            className="p-2 text-muted-foreground/30 hover:text-foreground/60 disabled:opacity-20 transition-colors"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
        {submitted && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-[10px] text-emerald-400/50 mt-1.5"
          >
            {t('whisper_sent') || 'Your whisper drifts into the wall.'}
          </motion.p>
        )}
        <p className="text-[9px] text-muted-foreground/10 mt-1">
          {inputValue.length}/100 · {t('anonymous') || 'anonymous'}
        </p>
      </div>
    </div>
  );
};

export default WhisperWall;
